import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Divider,
  TextField,
  CircularProgress,
  MenuItem,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  CalendarToday,
  Speed,
  Source as SourceIcon,
  Notes as NotesIcon,
  Category,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import updateIndexReading from "../../functions/updateIndexReading";
import { deleteIndexReading } from "../../API/IndexReadings";
import updateSnackbarMessage from "../../functions/updateSnackbarMessage";
import moment from "moment";

/**
 * Dialogue pour modifier un relevé d'index existant
 * @param {Object} props
 * @param {boolean} props.open - Dialogue ouvert ou fermé
 * @param {Function} props.onClose - Fonction appelée à la fermeture
 * @param {string} props.dev_eui - EUI du device
 * @param {Object} props.reading - Relevé à modifier
 * @param {Function} props.onSuccess - Fonction appelée après modification réussie
 */
export default function EditIndexReading({ open, onClose, dev_eui, reading, onSuccess }) {
  const { t } = useTranslation("common");

  const [formData, setFormData] = useState({
    index_value: "",
    reading_date: "",
    reading_type: "MANUAL",
    source: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with reading data when it changes
  useEffect(() => {
    if (reading) {
      setFormData({
        index_value: reading.index_value || "",
        reading_date: reading.reading_date ? moment(reading.reading_date).format("YYYY-MM-DDTHH:mm") : "",
        reading_type: reading.reading_type || "MANUAL",
        source: reading.source || "",
        notes: reading.notes || "",
      });
    }
  }, [reading]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Trim and replace comma with dot for European decimal format
    const cleanedValue = formData.index_value.toString().trim().replace(',', '.');

    if (!cleanedValue || isNaN(cleanedValue)) {
      newErrors.index_value = "L'index doit être un nombre valide";
    } else if (parseFloat(cleanedValue) < 0) {
      newErrors.index_value = "L'index ne peut pas être négatif";
    }

    if (!formData.reading_date) {
      newErrors.reading_date = "La date est requise";
    }

    if (!formData.source || !formData.source.trim()) {
      newErrors.source = "La source est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Clean the value: trim and replace comma with dot for European format
      const cleanedValue = formData.index_value.toString().trim().replace(',', '.');
      const indexValue = parseFloat(cleanedValue);

      const readingData = {
        index_value: indexValue,
        reading_date: moment(formData.reading_date).format("YYYY-MM-DDTHH:mm:ss"),
        source: formData.source.trim(),
        notes: formData.notes.trim() || null,
      };

      await updateIndexReading(dev_eui, reading.id, readingData);

      updateSnackbarMessage("Relevé d'index modifié avec succès");

      setErrors({});

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Erreur lors de la modification du relevé:", error);
      updateSnackbarMessage(
        error.response?.data?.detail ||
          "Erreur lors de la modification du relevé d'index"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce relevé d'index ?")) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteIndexReading(dev_eui, reading.id);

      updateSnackbarMessage("Relevé d'index supprimé avec succès");

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Erreur lors de la suppression du relevé:", error);
      updateSnackbarMessage(
        error.response?.data?.detail ||
          "Erreur lors de la suppression du relevé d'index"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ elevation: 3, sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ py: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <EditIcon sx={{ fontSize: 28, color: "#26A69A" }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Modifier le relevé d'index
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 5 }}>
          {dev_eui}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              label="Date et heure du relevé"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={formData.reading_date}
              onChange={handleChange("reading_date")}
              error={!!errors.reading_date}
              helperText={errors.reading_date || "Date et heure du relevé physique"}
              disabled={isLoading}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Index relevé"
              variant="outlined"
              type="text"
              fullWidth
              value={formData.index_value}
              onChange={handleChange("index_value")}
              error={!!errors.index_value}
              helperText={errors.index_value || "Valeur de l'index (ex: 1543.125)"}
              disabled={isLoading}
              placeholder="1543.125"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Speed sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Type de relevé"
              variant="outlined"
              select
              fullWidth
              value={formData.reading_type}
              disabled
              helperText="Type non modifiable"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Category sx={{ fontSize: 20, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="MANUAL">Manuel</MenuItem>
              <MenuItem value="AUTO">Automatique</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Source du relevé"
              variant="outlined"
              fullWidth
              value={formData.source}
              onChange={handleChange("source")}
              error={!!errors.source}
              helperText={
                errors.source ||
                'Ex: "Installation initiale", "Contrôle annuel"'
              }
              disabled={isLoading}
              placeholder="Installation initiale"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SourceIcon sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optionnel)"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange("notes")}
              helperText="Informations complémentaires"
              disabled={isLoading}
              placeholder="Compteur neuf installé, RAS, etc."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <NotesIcon sx={{ fontSize: 20, color: "#26A69A" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ mt: 2 }} />
      <DialogActions sx={{ px: 3, py: 2.5, justifyContent: "space-between" }}>
        <Button
          onClick={handleDelete}
          startIcon={<DeleteIcon />}
          sx={{
            fontWeight: "medium",
            px: 3,
            color: "#f44336",
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.1)"
            }
          }}
          disabled={isLoading}
        >
          Supprimer
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              fontWeight: "medium",
              px: 3,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.05)"
              }
            }}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              fontWeight: "medium",
              px: 4,
              backgroundColor: "#26A69A",
              color: "black",
              "&:hover": {
                backgroundColor: "#00897B",
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "black" }} />
            ) : (
              "Modifier"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
